import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import { filterDuplicateElements } from '@/store/slices/components/calendarEvents/utils';
import { LINKED_ENTITY_TYPE_BY_KEY } from '@/store/slices/components/fileManager/utils';
import {
  checkUploadDuplicates,
  createFileLink,
  uploadEntityFile,
  uploadFileBatch,
} from '@/store/slices/components/fileManager/services';
import {
  getAddFileModalContacts,
  getAddFileModalDevelopments,
  getAddFileModalProperties,
} from './services';

const ENTITY_KEYS = ['properties', 'developments', 'contacts'];

const LINKED_FIELD_BY_ENTITY = {
  properties: 'linkedProperties',
  developments: 'linkedDevelopments',
  contacts: 'linkedContacts',
};

export const isOriginEntity = (originEntity, entityKey, itemId) =>
  Boolean(originEntity) &&
  LINKED_ENTITY_TYPE_BY_KEY[entityKey] === originEntity.entityType &&
  String(itemId) === String(originEntity.entityId);

const normalizeOriginEntity = (originEntity) =>
  originEntity?.entityType && originEntity?.entityId != null
    ? {
        entityType: String(originEntity.entityType).toUpperCase(),
        entityId: originEntity.entityId,
      }
    : null;

const withShowInWebDefault = (items = []) =>
  (items ?? [])
    .filter((item) => item?.id != null)
    .map((item) => ({
      ...item,
      showInWeb: item.showInWeb ?? false,
    }));

export const AddFileModalSlice = createSlice({
  name: 'AddFileModal',
  initialState,
  reducers: {
    openAddFileModal(state, action) {
      state.isOpen = true;
      state.locationLabel = action.payload?.locationLabel ?? '';
      state.locationTrail = (action.payload?.locationTrail ?? []).filter(
        (folderName) => typeof folderName === 'string' && folderName !== ''
      );
      state.requestMode = Boolean(action.payload?.requestMode);
      state.mode = action.payload?.mode === 'link' ? 'link' : 'file';
      state.originEntity = normalizeOriginEntity(action.payload?.originEntity);
      state.linkedContacts = action.payload?.linkedContacts ?? [];
      state.linkedProperties = withShowInWebDefault(
        action.payload?.linkedProperties
      );
      state.linkedDevelopments = withShowInWebDefault(
        action.payload?.linkedDevelopments
      );
    },
    closeAddFileModal() {
      return { ...initialState };
    },
    clearEntityResults(state, action) {
      const { entityKey } = action.payload;
      if (!ENTITY_KEYS.includes(entityKey)) return;
      state[entityKey].data = [];
      state[entityKey].loading = false;
      state[entityKey].error = null;
    },
    addEntity(state, action) {
      const { entityKey, item } = action.payload;
      if (!ENTITY_KEYS.includes(entityKey)) return;

      const linkedField = LINKED_FIELD_BY_ENTITY[entityKey];
      const itemWithDefaults =
        entityKey === 'contacts'
          ? item
          : { ...item, showInWeb: item.showInWeb ?? false };

      state[linkedField] = [...state[linkedField], itemWithDefaults];
      state[entityKey].data = state[entityKey].data.filter(
        (current) => current?.id !== item?.id
      );
    },
    toggleEntityShowInWeb(state, action) {
      const { entityKey, itemId } = action.payload;
      if (!ENTITY_KEYS.includes(entityKey)) return;

      const linkedField = LINKED_FIELD_BY_ENTITY[entityKey];
      state[linkedField] = state[linkedField].map((current) =>
        current?.id === itemId
          ? { ...current, showInWeb: !current.showInWeb }
          : current
      );
    },
    removeEntity(state, action) {
      const { entityKey, item } = action.payload;
      if (!ENTITY_KEYS.includes(entityKey)) return;

      const linkedField = LINKED_FIELD_BY_ENTITY[entityKey];
      const itemId = item?.id;
      if (isOriginEntity(state.originEntity, entityKey, itemId)) return;
      state[linkedField] = state[linkedField].filter((current) =>
        itemId ? current?.id !== itemId : current !== item
      );
    },
    resetSaveStatus(state) {
      if (state.saveStatus === 'failed') {
        state.saveStatus = 'idle';
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkUploadDuplicates.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(checkUploadDuplicates.fulfilled, (state) => {
        state.saveStatus = 'idle';
      })
      .addCase(checkUploadDuplicates.rejected, (state) => {
        state.saveStatus = 'failed';
      })

      .addCase(createFileLink.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(createFileLink.fulfilled, (state) => {
        if (state.saveStatus === 'loading') {
          return { ...initialState };
        }

        return undefined;
      })
      .addCase(createFileLink.rejected, (state) => {
        state.saveStatus = 'failed';
      })

      .addCase(uploadEntityFile.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(uploadEntityFile.fulfilled, (state) => {
        if (state.saveStatus === 'loading') {
          return { ...initialState };
        }

        return undefined;
      })
      .addCase(uploadEntityFile.rejected, (state) => {
        state.saveStatus = 'failed';
      })

      .addCase(uploadFileBatch.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(uploadFileBatch.fulfilled, (state, action) => {
        if (state.saveStatus === 'loading') {
          // Everything was cancelled: keep the modal open for a new selection.
          if (action.payload?.allCancelled) {
            state.saveStatus = 'idle';
            return undefined;
          }

          return { ...initialState };
        }

        return undefined;
      })
      .addCase(uploadFileBatch.rejected, (state) => {
        state.saveStatus = 'failed';
      })

      .addCase(getAddFileModalContacts.pending, (state) => {
        state.contacts.loading = true;
        state.contacts.error = null;
      })
      .addCase(getAddFileModalContacts.fulfilled, (state, action) => {
        state.contacts.loading = false;
        state.contacts.data = filterDuplicateElements(
          action.payload.contacts ?? [],
          state.linkedContacts
        );
      })
      .addCase(getAddFileModalContacts.rejected, (state) => {
        state.contacts.loading = false;
        state.contacts.error = true;
      })

      .addCase(getAddFileModalProperties.pending, (state) => {
        state.properties.loading = true;
        state.properties.error = null;
      })
      .addCase(getAddFileModalProperties.fulfilled, (state, action) => {
        state.properties.loading = false;
        state.properties.data = filterDuplicateElements(
          action.payload.properties ?? [],
          state.linkedProperties
        );
      })
      .addCase(getAddFileModalProperties.rejected, (state) => {
        state.properties.loading = false;
        state.properties.error = true;
      })

      .addCase(getAddFileModalDevelopments.pending, (state) => {
        state.developments.loading = true;
        state.developments.error = null;
      })
      .addCase(getAddFileModalDevelopments.fulfilled, (state, action) => {
        state.developments.loading = false;
        state.developments.data = filterDuplicateElements(
          action.payload.results ?? [],
          state.linkedDevelopments
        );
      })
      .addCase(getAddFileModalDevelopments.rejected, (state) => {
        state.developments.loading = false;
        state.developments.error = true;
      });
  },
});

export const {
  openAddFileModal,
  closeAddFileModal,
  clearEntityResults,
  addEntity,
  removeEntity,
  toggleEntityShowInWeb,
  resetSaveStatus,
} = AddFileModalSlice.actions;

export const selectAddFileModalIsOpen = (state) =>
  state.AddFileModalSlice.isOpen;

export const selectAddFileModalSaveStatus = (state) =>
  state.AddFileModalSlice.saveStatus;

export const selectAddFileModalSearchStates = (state) => ({
  properties: state.AddFileModalSlice.properties,
  developments: state.AddFileModalSlice.developments,
  contacts: state.AddFileModalSlice.contacts,
});

export const selectAddFileModalOriginEntity = (state) =>
  state.AddFileModalSlice.originEntity;

export const selectAddFileModalLinkedEntities = (state) => ({
  linkedContacts: state.AddFileModalSlice.linkedContacts,
  linkedProperties: state.AddFileModalSlice.linkedProperties,
  linkedDevelopments: state.AddFileModalSlice.linkedDevelopments,
});

export default AddFileModalSlice.reducer;
